/**
 * Faction patterns (M8 plan MC-0b contract; MC-f1 fills `patterns.json` and adds the schema rules). A pattern is a
 * Mk II/III sidegrade: one strength and one cost, each a signed fraction of one existing module stat, applied to the
 * base def of every module kind it lists (MC-f2a's `resolveModule`). Kept out of `m2Schemas.ts` so the strict M2
 * files stay untouched. Nothing in normal play reads it until MC-f2a/MC-f3.
 *
 * Rules (plan MC-f1, research R3 §5.4):
 * - **Equal power budget:** both shifts lie in `budget.minFrac..maxFrac`, and | |strength| − |cost| | ≤ `budget.tolerance`.
 * - **Existing stat keys only:** each stat is a shiftable numeric key of every listed kind's stats, or `power` (the
 *   module's power draw; not on power plants, which draw none). The strength helps and the cost hurts
 *   (`STAT_DIRECTION`).
 * - **Routes:** a standing route iff a standing faction, and that faction is the pattern's maker. `holdfast_old` has no
 *   standing (or purchase) route, is made by the `first_charter` and needs a salvaged component as its input.
 * - **Never tetherstone** as the signature input.
 */
import { z } from 'zod'
import { GOOD_IDS } from '../career/parts'
import { MAKER_IDS, PATTERN_IDS } from '../career/types'
import { COMPONENT_TYPES, MODULE_KINDS, MODULE_STATS_SCHEMAS, SIM_FACTION_IDS, WARE_IDS, type ModuleKind } from './m2Schemas'
import patternsJson from './patterns.json'

/** How a pattern unlocks (MC-f3): faction standing, studying salvaged components, or blueprint fragments (MC-h5a). */
export const PATTERN_ROUTES = ['standing', 'study', 'fragments'] as const
export type PatternRoute = (typeof PATTERN_ROUTES)[number]

/**
 * The stats a pattern may shift and which way is better: +1 = higher is better, −1 = lower is better. `power` is the
 * module's power draw. Left out: `maxPower` (capped at 1), `maxTier` (an integer tier) and `accuracy` (an enum).
 */
export const STAT_DIRECTION = {
  damageMult: 1,
  heatCapacityMult: 1,
  chipRateMult: 1,
  cutMult: -1,
  spillPerSec: -1,
  tractorRange: 1,
  capacityT: 1,
  maxPartMassT: 1,
  shieldMult: 1,
  regenMult: 1,
  budget: 1,
  thrustMult: 1,
  maxSpeedAdd: 1,
  range: 1,
  power: -1,
} as const satisfies Record<string, 1 | -1>
export type PatternStat = keyof typeof STAT_DIRECTION

/** Whether `stat` can be shifted on a module of `kind`. */
export function statFitsKind(stat: string, kind: ModuleKind): stat is PatternStat {
  if (!Object.hasOwn(STAT_DIRECTION, stat)) return false
  if (stat === 'power') return kind !== 'power'
  return Object.hasOwn(MODULE_STATS_SCHEMAS[kind].shape, stat)
}

/** Everything a pattern's signature input may be: a good, a raw ware or a salvaged component (`FabCarryId`). */
const SIGNATURE_INPUTS = [...GOOD_IDS, ...WARE_IDS, ...COMPONENT_TYPES] as const

/** One stat change of a pattern: an existing module stat key and its signed fraction (0.15 = +15%). */
const statShiftSchema = z.strictObject({
  stat: z.string().min(1),
  frac: z.number().min(-1).max(1),
})

export const patternSchema = z.strictObject({
  /** Display name (a placeholder, research R3 §5.4). */
  name: z.string().min(1),
  /** The faction whose standing unlocks it; null for a pattern with no standing route (`holdfast_old`). */
  standingFaction: z.enum(SIM_FACTION_IDS).nullable(),
  /** The maker whose salvaged components study it. */
  maker: z.enum(MAKER_IDS),
  routes: z.array(z.enum(PATTERN_ROUTES)).min(1),
  /** The module kinds it covers (Mk II and Mk III only). */
  modules: z.array(z.enum(MODULE_KINDS)).min(1),
  strength: statShiftSchema,
  cost: statShiftSchema,
  /** The pattern's signature input (a good, ware or salvage component id; never tetherstone). */
  signatureInput: z.enum(SIGNATURE_INPUTS),
})
export type PatternDef = z.infer<typeof patternSchema>

type Path = (string | number)[]

export const patternsFileSchema = z
  .strictObject({
    /** Standing needed for the standing route, per rung. */
    standingReq: z.strictObject({ mk2: z.number().int().nonnegative(), mk3: z.number().int().nonnegative() }),
    /** The equal power budget: the size of each shift, and how far |strength| and |cost| may differ. */
    budget: z.strictObject({
      minFrac: z.number().gt(0).max(1),
      maxFrac: z.number().gt(0).max(1),
      tolerance: z.number().nonnegative().max(1),
    }),
    /** Patterns by id (`PATTERN_IDS`); a missing id has no pattern yet. */
    patterns: z.partialRecord(z.enum(PATTERN_IDS), patternSchema),
  })
  .superRefine((f, ctx) => {
    const { minFrac, maxFrac, tolerance } = f.budget
    if (minFrac > maxFrac) ctx.addIssue({ code: 'custom', path: ['budget'], message: 'minFrac must not exceed maxFrac' })
    for (const id of PATTERN_IDS) {
      const p = f.patterns[id]
      if (!p) continue
      const issue = (rest: Path, message: string): void => {
        ctx.addIssue({ code: 'custom', path: ['patterns', id, ...rest], message: `${id}: ${message}` })
      }

      if (new Set(p.routes).size !== p.routes.length) issue(['routes'], 'a route appears more than once')
      if (new Set(p.modules).size !== p.modules.length) issue(['modules'], 'a module kind appears more than once')

      const standing = p.routes.includes('standing')
      if (standing !== (p.standingFaction !== null)) issue(['standingFaction'], 'a standing route needs a standing faction, and only then')
      if (p.standingFaction !== null && p.standingFaction !== p.maker) {
        issue(['standingFaction'], `standing faction ${p.standingFaction} is not the maker ${p.maker}`)
      }

      if (id === 'holdfast_old') {
        if (standing) issue(['routes'], 'holdfast_old has no standing route')
        if (p.maker !== 'first_charter') issue(['maker'], 'holdfast_old is made by the first_charter')
        if (!(COMPONENT_TYPES as readonly string[]).includes(p.signatureInput)) {
          issue(['signatureInput'], 'holdfast_old needs a salvaged component as its input')
        }
      }
      if (p.signatureInput === 'tetherstone') issue(['signatureInput'], 'tetherstone is in no pattern')

      if (p.strength.stat === p.cost.stat) issue(['cost', 'stat'], 'strength and cost shift the same stat')
      const shifts = [
        ['strength', p.strength, 1],
        ['cost', p.cost, -1],
      ] as const
      for (const [role, shift, want] of shifts) {
        const bad = p.modules.filter((kind) => !statFitsKind(shift.stat, kind))
        if (bad.length > 0) {
          issue([role, 'stat'], `"${shift.stat}" is not a shiftable stat of ${bad.join(', ')}`)
          continue
        }
        const size = Math.abs(shift.frac)
        if (size < minFrac || size > maxFrac) issue([role, 'frac'], `${role} ${shift.frac} is outside ±${minFrac}..${maxFrac}`)
        const helps = Math.sign(shift.frac) * STAT_DIRECTION[shift.stat as PatternStat]
        if (helps !== want) issue([role, 'frac'], `the ${role} must ${want > 0 ? 'help' : 'hurt'} (${shift.stat} ${shift.frac})`)
      }
      if (Math.abs(Math.abs(p.strength.frac) - Math.abs(p.cost.frac)) > tolerance + 1e-9) {
        issue(['cost', 'frac'], `unequal power budget: |${p.strength.frac}| vs |${p.cost.frac}| (tolerance ${tolerance})`)
      }
    }
  })
export type PatternsFile = z.infer<typeof patternsFileSchema>

/** Parses `patterns.json`; throws naming the bad path. */
export function loadPatterns(raw: unknown = patternsJson): PatternsFile {
  return patternsFileSchema.parse(raw)
}
